import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import './GroupManagement.css';

const GroupManagement = ({ conversation, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('members');
  const [members, setMembers] = useState(conversation.participants || []);
  const [admins, setAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [memberRoles, setMemberRoles] = useState([]);
  const [groupName, setGroupName] = useState(conversation.groupName || '');
  const [groupDescription, setGroupDescription] = useState(conversation.groupDescription || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showCreateRole, setShowCreateRole] = useState(false);
  const [showEditRole, setShowEditRole] = useState(null);
  const fileInputRef = useRef(null);

  // Get current user from AuthContext
  const { user } = useContext(AuthContext);
  const currentUserId = user?._id;

  // Handle both populated and unpopulated groupCreatedBy
  const isCreator = conversation.groupCreatedBy?._id?.toString() === currentUserId?.toString() || conversation.groupCreatedBy?.toString() === currentUserId?.toString();

  useEffect(() => {
    if (conversation.isGroup) {
      fetchAdmins();
      fetchRoles();
      fetchMemberRoles();
    }
  }, [conversation._id]);

  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const fetchAdmins = async () => {
    try {
      const response = await fetch(`/api/messages/groups/${conversation._id}/admins`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setAdmins(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching admins:', err);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await fetch(`/api/messages/groups/${conversation._id}/roles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
    }
  };

  const fetchMemberRoles = async () => {
    try {
      const response = await fetch(`/api/messages/groups/${conversation._id}/member-roles`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setMemberRoles(data.data || []);
      }
    } catch (err) {
      console.error('Error fetching member roles:', err);
    }
  };

  const searchUsers = async () => {
    try {
      const response = await fetch(`/api/profile/search?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        // Filter out users already in group
        const filtered = (data.users || []).filter(
          u => !members.find(m => m._id === u._id)
        );
        setSearchResults(filtered);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    }
  };

  const updateGroupSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/messages/groups/${conversation._id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          groupName,
          groupDescription
        })
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate(data.data);
        alert('Group settings updated successfully!');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to update group');
      }
    } catch (err) {
      setError('Failed to update group');
    } finally {
      setLoading(false);
    }
  };

  const addMember = async (userId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/members`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ userIds: [userId] })
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate(data.data);
        setMembers(data.data.participants);
        setSearchQuery('');
        setSearchResults([]);
      }
    } catch (err) {
      alert('Failed to add member');
    } finally {
      setLoading(false);
    }
  };

  const removeMember = async (userId) => {
    if (!window.confirm('Remove this member from the group?')) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/members/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setMembers(prev => prev.filter(m => m._id !== userId));
        alert('Member removed successfully');
      }
    } catch (err) {
      alert('Failed to remove member');
    } finally {
      setLoading(false);
    }
  };

  const promoteToAdmin = async (userId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/admins/${userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          permissions: {
            canAddMembers: true,
            canRemoveMembers: true,
            canEditGroup: true,
            canDeleteMessages: true,
            canPinMessages: true,
            canManageAdmins: false
          }
        })
      });

      if (response.ok) {
        fetchAdmins();
        alert('User promoted to admin!');
      }
    } catch (err) {
      alert('Failed to promote user');
    } finally {
      setLoading(false);
    }
  };

  const demoteAdmin = async (userId) => {
    if (!window.confirm('Remove admin privileges from this user?')) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/admins/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchAdmins();
        alert('Admin demoted successfully');
      }
    } catch (err) {
      alert('Failed to demote admin');
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = (userId) => admins.some(a => a.userId._id === userId || a.userId._id?.toString() === userId?.toString());
  // Handle both populated and unpopulated groupCreatedBy
  const isGroupCreator = (userId) => {
    const creatorId = conversation.groupCreatedBy?._id?.toString() || conversation.groupCreatedBy?.toString();
    return userId?.toString() === creatorId;
  };

  // Check if current user has specific permission through roles
  const hasRolePermission = (permission) => {
    const userRoles = memberRoles.filter(mr => 
      mr.userId._id?.toString() === currentUserId?.toString() || 
      mr.userId._id === currentUserId
    );
    return userRoles.some(mr => mr.roleId?.permissions?.[permission] === true);
  };

  // Check if user can edit group (creator, admin, or has role permission)
  const canEditGroup = () => {
    return isCreator || isAdmin(currentUserId) || hasRolePermission('canEditGroup');
  };

  // Check if user can manage roles
  const canManageRoles = () => {
    return isCreator || hasRolePermission('canManageRoles');
  };

  // Check if user can manage members
  const canManageMembers = () => {
    return isCreator || isAdmin(currentUserId) || hasRolePermission('canRemoveMembers');
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      setError(null);

      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`/api/messages/groups/${conversation._id}/avatar`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        onUpdate(data.data);
        alert('Group avatar updated successfully!');
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to upload avatar');
      }
    } catch (err) {
      console.error('Upload avatar error:', err);
      setError('Failed to upload avatar');
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleLeaveGroup = async () => {
    if (isCreator) {
      if (!window.confirm('As the creator, leaving will transfer ownership to another admin. Continue?')) {
        return;
      }
    } else {
      if (!window.confirm('Are you sure you want to leave this group?')) {
        return;
      }
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/members/${currentUserId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('You have left the group');
        onClose();
        // Refresh the parent component
        if (onUpdate) {
          onUpdate(null);
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to leave group');
      }
    } catch (err) {
      console.error('Leave group error:', err);
      setError('Failed to leave group');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm('Are you sure you want to delete this group? This action cannot be undone and all messages will be deleted.')) {
      return;
    }

    // Double confirmation
    if (!window.confirm('This will permanently delete the group and all its data. Are you absolutely sure?')) {
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        alert('Group deleted successfully');
        onClose();
        // Refresh the parent component
        if (onUpdate) {
          onUpdate(null);
        }
      } else {
        const data = await response.json();
        setError(data.message || 'Failed to delete group');
      }
    } catch (err) {
      console.error('Delete group error:', err);
      setError('Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  // Role Management Functions
  const getMemberRole = (userId) => {
    const assignment = memberRoles.find(mr => mr.userId._id === userId);
    return assignment?.roleId;
  };

  const assignRole = async (userId, roleId) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/members/${userId}/roles/${roleId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchMemberRoles();
        alert('Role assigned successfully!');
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to assign role');
      }
    } catch (err) {
      alert('Failed to assign role');
    } finally {
      setLoading(false);
    }
  };

  const removeRole = async (userId, roleId) => {
    if (!window.confirm('Remove this role from the member?')) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/members/${userId}/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchMemberRoles();
        alert('Role removed successfully');
      }
    } catch (err) {
      alert('Failed to remove role');
    } finally {
      setLoading(false);
    }
  };

  const createRole = async (roleData) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(roleData)
      });

      if (response.ok) {
        fetchRoles();
        setShowCreateRole(false);
        alert('Role created successfully!');
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to create role');
      }
    } catch (err) {
      alert('Failed to create role');
    } finally {
      setLoading(false);
    }
  };

  const updateRole = async (roleId, roleData) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/roles/${roleId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(roleData)
      });

      if (response.ok) {
        fetchRoles();
        setShowEditRole(null);
        alert('Role updated successfully!');
      } else {
        const data = await response.json();
        alert(data.message || 'Failed to update role');
      }
    } catch (err) {
      alert('Failed to update role');
    } finally {
      setLoading(false);
    }
  };

  const deleteRole = async (roleId) => {
    if (!window.confirm('Delete this role? All members with this role will lose it.')) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/messages/groups/${conversation._id}/roles/${roleId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        fetchRoles();
        fetchMemberRoles();
        alert('Role deleted successfully');
      }
    } catch (err) {
      alert('Failed to delete role');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="group-management-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Group Settings</h2>
          <button onClick={onClose} className="modal-close-btn">✕</button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <div className="group-tabs">
          <button
            className={activeTab === 'members' ? 'active' : ''}
            onClick={() => setActiveTab('members')}
          >
            👥 Members ({members.length})
          </button>
          <button
            className={activeTab === 'roles' ? 'active' : ''}
            onClick={() => setActiveTab('roles')}
          >
            🎭 Roles ({roles.length})
          </button>
          <button
            className={activeTab === 'admins' ? 'active' : ''}
            onClick={() => setActiveTab('admins')}
          >
            👑 Admins ({admins.length})
          </button>
          <button
            className={activeTab === 'settings' ? 'active' : ''}
            onClick={() => setActiveTab('settings')}
          >
            ⚙️ Settings
          </button>
        </div>

        <div className="group-tab-content">
          {activeTab === 'members' && (
            <div className="members-tab">
              <div className="add-member-section">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search users to add..."
                  className="search-input"
                />
                {searchResults.length > 0 && (
                  <div className="search-results-dropdown">
                    {searchResults.map(user => (
                      <div key={user._id} className="search-result-item">
                        <span>{user.username}</span>
                        <button onClick={() => addMember(user._id)} disabled={loading}>
                          + Add
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="members-list">
                {members.map(member => (
                  <div key={member._id} className="member-item">
                    <div className="member-avatar">
                      {member.profile?.avatar?.url ? (
                        <img src={member.profile.avatar.url} alt={member.username} />
                      ) : (
                        <div className="avatar-placeholder">{member.username[0].toUpperCase()}</div>
                      )}
                    </div>
                    <div className="member-info">
                      <div className="member-name">
                        {member.username}
                        {isGroupCreator(member._id) && ' 👑'}
                        {isAdmin(member._id) && !isGroupCreator(member._id) && ' ⭐'}
                      </div>
                      <div className="member-roles-badges">
                        {memberRoles
                          .filter(mr => mr.userId._id === member._id)
                          .map(mr => (
                            <span
                              key={mr._id}
                              className="member-role-badge-small"
                              style={{ backgroundColor: mr.roleId.color }}
                            >
                              {mr.roleId.icon} {mr.roleId.name}
                            </span>
                          ))}
                      </div>
                      <div className="member-status">{member.accountType || 'member'}</div>
                    </div>
                    {!isGroupCreator(member._id) && canManageMembers() && (
                      <button
                        className="remove-member-btn"
                        onClick={() => removeMember(member._id)}
                        disabled={loading}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'roles' && (
            <div className="roles-tab">
              <div className="roles-header">
                <h3>Custom Roles</h3>
                {canManageRoles() && (
                  <button
                    onClick={() => setShowCreateRole(true)}
                    className="create-role-btn"
                    disabled={loading}
                  >
                    ✨ Create Role
                  </button>
                )}
              </div>

              {showCreateRole && (
                <RoleEditor
                  onSave={(roleData) => createRole(roleData)}
                  onCancel={() => setShowCreateRole(false)}
                  loading={loading}
                />
              )}

              <div className="roles-list">
                {roles.length > 0 ? (
                  roles.map(role => (
                    <div key={role._id} className="role-item">
                      {showEditRole === role._id ? (
                        <RoleEditor
                          role={role}
                          onSave={(roleData) => updateRole(role._id, roleData)}
                          onCancel={() => setShowEditRole(null)}
                          loading={loading}
                        />
                      ) : (
                        <>
                          <div className="role-header-info">
                            <div className="role-badge" style={{ backgroundColor: role.color }}>
                              {role.icon} {role.name}
                              {role.isDefault && role.name === 'Owner' && (
                                <span className="role-default-badge">DEFAULT</span>
                              )}
                            </div>
                            <div className="role-actions">
                              {canManageRoles() && (
                                <>
                                  <button
                                    onClick={() => setShowEditRole(role._id)}
                                    className="role-edit-btn"
                                    disabled={loading}
                                  >
                                    ✏️
                                  </button>
                                  {/* Don't allow deletion of default Owner role */}
                                  {!(role.isDefault && role.name === 'Owner') && (
                                    <button
                                      onClick={() => deleteRole(role._id)}
                                      className="role-delete-btn"
                                      disabled={loading}
                                    >
                                      🗑️
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </div>

                          <div className="role-permissions-list">
                            {Object.entries(role.permissions)
                              .filter(([_, value]) => value)
                              .map(([key]) => (
                                <span key={key} className="permission-badge">
                                  {formatPermissionName(key)}
                                </span>
                              ))}
                          </div>

                          <div className="role-members-section">
                            <h4>Members with this role</h4>
                            <div className="role-members-list">
                              {memberRoles
                                .filter(mr => mr.roleId._id === role._id)
                                .map(mr => (
                                  <div key={mr._id} className="role-member-item">
                                    <span>
                                      {mr.userId.username}
                                      {/* Show icon if this is the creator with Owner role */}
                                      {role.isDefault && role.name === 'Owner' && isGroupCreator(mr.userId._id) && ' 👑'}
                                    </span>
                                    {canManageRoles() && (
                                      // Don't allow removal of Owner role from the creator
                                      !(role.isDefault && role.name === 'Owner' && isGroupCreator(mr.userId._id)) && (
                                        <button
                                          onClick={() => removeRole(mr.userId._id, role._id)}
                                          className="remove-role-btn-small"
                                          disabled={loading}
                                        >
                                          ✕
                                        </button>
                                      )
                                    )}
                                  </div>
                                ))}
                            </div>

                            {canManageRoles() && (
                              <div className="assign-role-section">
                                <select
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      assignRole(e.target.value, role._id);
                                      e.target.value = '';
                                    }
                                  }}
                                  className="assign-role-select"
                                  disabled={loading}
                                >
                                  <option value="">Assign to member...</option>
                                  {members
                                    .filter(m => !memberRoles.some(mr => mr.userId._id === m._id && mr.roleId._id === role._id))
                                    .map(member => (
                                      <option key={member._id} value={member._id}>
                                        {member.username}
                                      </option>
                                    ))}
                                </select>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">
                    <p>No custom roles yet.</p>
                    {canManageRoles() && <p>Create roles to give members specific permissions!</p>}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'admins' && (
            <div className="admins-tab">
              <div className="admins-list">
                {admins.length > 0 ? (
                  admins.map(admin => (
                    <div key={admin._id} className="admin-item">
                      <div className="admin-info">
                        <div className="admin-name">
                          {admin.userId.username}
                          {isGroupCreator(admin.userId._id) && ' 👑'}
                          <span className={isGroupCreator(admin.userId._id) ? "admin-role-badge owner" : "admin-role-badge"}>
                            {isGroupCreator(admin.userId._id) ? 'Owner' : 'Admin'}
                          </span>
                        </div>
                        <div className="admin-permissions">
                          {Object.entries(admin.permissions)
                            .filter(([_, value]) => value)
                            .map(([key]) => key.replace('can', ''))
                            .join(', ')}
                        </div>
                      </div>
                      {!isGroupCreator(admin.userId._id) && isCreator && (
                        <button
                          className="demote-btn"
                          onClick={() => demoteAdmin(admin.userId._id)}
                          disabled={loading}
                        >
                          Demote
                        </button>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="empty-state">No admins yet</div>
                )}
              </div>

              {isCreator && (
                <div className="promote-section">
                  <h4>Promote Member to Admin</h4>
                  <div className="members-promote-list">
                    {members
                      .filter(m => !isAdmin(m._id) && !isGroupCreator(m._id))
                      .map(member => (
                        <div key={member._id} className="promote-item">
                          <span>{member.username}</span>
                          <button onClick={() => promoteToAdmin(member._id)} disabled={loading}>
                            Promote
                          </button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'settings' && (
            <div className="settings-tab">
              <div className="form-group">
                <label>Group Avatar</label>
                <div className="avatar-upload-container">
                  <div className="current-avatar">
                    {conversation.groupAvatar ? (
                      <img src={conversation.groupAvatar} alt="Group avatar" className="group-avatar-preview" />
                    ) : (
                      <div className="avatar-placeholder-large">
                        {groupName[0]?.toUpperCase() || 'G'}
                      </div>
                    )}
                  </div>
                  {canEditGroup() && (
                    <div className="avatar-upload-actions">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleAvatarUpload}
                        style={{ display: 'none' }}
                      />
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="upload-avatar-btn"
                        disabled={uploadingAvatar}
                      >
                        {uploadingAvatar ? '📤 Uploading...' : '📷 Change Avatar'}
                      </button>
                      <span className="avatar-hint">Max 5MB, JPG/PNG</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Group Name</label>
                <input
                  type="text"
                  value={groupName}
                  onChange={e => setGroupName(e.target.value)}
                  maxLength={100}
                  disabled={!canEditGroup()}
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={groupDescription}
                  onChange={e => setGroupDescription(e.target.value)}
                  maxLength={500}
                  rows={4}
                  disabled={!canEditGroup()}
                />
              </div>

              {canEditGroup() && (
                <button
                  onClick={updateGroupSettings}
                  className="save-settings-btn"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Changes'}
                </button>
              )}

              {/* Danger Zone */}
              <div className="danger-zone">
                <h3>Danger Zone</h3>
                <div className="danger-actions">
                  <button
                    onClick={handleLeaveGroup}
                    className="leave-group-btn"
                    disabled={loading}
                  >
                    🚪 Leave Group
                  </button>
                  {isCreator && (
                    <button
                      onClick={handleDeleteGroup}
                      className="delete-group-btn"
                      disabled={loading}
                    >
                      🗑️ Delete Group
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Helper function to format permission names
const formatPermissionName = (permissionKey) => {
  return permissionKey
    .replace(/^can/, '')
    .replace(/([A-Z])/g, ' $1')
    .trim();
};

// Role Editor Component
const RoleEditor = ({ role, onSave, onCancel, loading }) => {
  const [name, setName] = useState(role?.name || '');
  const [color, setColor] = useState(role?.color || '#667eea');
  const [icon, setIcon] = useState(role?.icon || '⭐');
  const [priority, setPriority] = useState(role?.priority || 0);
  const [showColorDropdown, setShowColorDropdown] = useState(false);
  const [permissions, setPermissions] = useState(role?.permissions || {
    canAddMembers: false,
    canRemoveMembers: false,
    canManageRoles: false,
    canEditGroup: false,
    canDeleteGroup: false,
    canDeleteMessages: false,
    canPinMessages: false,
    canManageMessages: false,
    canSendMessages: true,
    canSendMedia: true,
    canMentionEveryone: false
  });

  const commonIcons = ['⭐', '🛡️', '⚔️', '👮', '🎯', '💎', '🔥', '⚡', '🌟', '👑', '🎭', '🏆'];
  const commonColors = ['#667eea', '#764ba2', '#f093fb', '#4facfe', '#43e97b', '#fa709a', '#feca57', '#ff6348'];

  const togglePermission = (key) => {
    setPermissions(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const handleColorSelect = (selectedColor) => {
    setColor(selectedColor);
    setShowColorDropdown(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      alert('Role name is required');
      return;
    }
    onSave({ name, color, icon, priority, permissions });
  };

  return (
    <div className="role-editor">
      <form onSubmit={handleSubmit}>
        <div className="role-editor-header">
          <h4>{role ? 'Edit Role' : 'Create New Role'}</h4>
        </div>

        <div className="role-editor-row">
          <div className="form-group">
            <label>Role Name *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Moderator, Helper"
              maxLength={50}
              required
            />
          </div>

          <div className="form-group">
            <label>Priority (higher = more important)</label>
            <input
              type="number"
              value={priority}
              onChange={(e) => setPriority(Number(e.target.value))}
              min={0}
              max={100}
            />
          </div>
        </div>

        <div className="role-editor-row">
          <div className="form-group">
            <label>Icon</label>
            <div className="icon-picker">
              {commonIcons.map(i => (
                <button
                  key={i}
                  type="button"
                  className={`icon-option ${icon === i ? 'selected' : ''}`}
                  onClick={() => setIcon(i)}
                >
                  {i}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              maxLength={2}
              placeholder="Or type custom emoji"
            />
          </div>

          <div className="form-group">
            <label>Color</label>
            <div className="color-picker-container">
              <button
                type="button"
                className="color-trigger-btn"
                onClick={() => setShowColorDropdown(!showColorDropdown)}
                style={{ backgroundColor: color }}
              >
                <span className="color-trigger-text">Click to choose color</span>
              </button>
              {showColorDropdown && (
                <div className="color-picker-dropdown">
                  {commonColors.map(c => (
                    <button
                      key={c}
                      type="button"
                      className={`color-option ${color === c ? 'selected' : ''}`}
                      style={{ backgroundColor: c }}
                      onClick={() => handleColorSelect(c)}
                    />
                  ))}
                </div>
              )}
            </div>
            <input
              type="color"
              value={color}
              onChange={(e) => setColor(e.target.value)}
              style={{ marginTop: '8px' }}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Permissions</label>
          <div className="permissions-grid">
            {Object.entries(permissions).map(([key, value]) => (
              <label key={key} className="permission-checkbox">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={() => togglePermission(key)}
                />
                <span>{formatPermissionName(key)}</span>
              </label>
            ))}
          </div>
        </div>

        <div className="role-editor-actions">
          <button type="button" onClick={onCancel} className="cancel-btn" disabled={loading}>
            Cancel
          </button>
          <button type="submit" className="save-role-btn" disabled={loading}>
            {loading ? 'Saving...' : role ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default GroupManagement;
